import { useState, useEffect, useRef } from 'react';
import { Auth } from 'aws-amplify';
import {
  ValidateEmail,
  ValidatePassword,
} from '../../../shared/AccountPolicy/AccountPolicy';
import sharedHistory from '../../../shared/History';
import { withNavBar } from '../../shared/NavBar/NavBar';
import useLocalization from '../../../shared/useLocalization/useLocalization';

import './forgotPasswordCode.scss';
import { RouteNames } from '../../../shared/enums/RouteNames';
import { getQuery } from '../../../shared/pageUtilities/pageUtilities';
import { ValidationClasses } from '../../../shared/enums/ValidationClasses';
import { PasswordInput } from '../../shared/PasswordInput/PasswordInput';

export const FormIds = {
  username: 'Username',
  email: 'Email',
  resetPasswordBtn: 'ResetPassword',
  cancelBtn: 'Cancel',
  password: 'password',
  passwordConfirm: 'passwordConfirm',
  code: 'code',
};

const VALID = 'valid';

const ForgotPasswordCode = () => {
  const local = useLocalization();
  const [resetPasswordErrorMessage, setResetPasswordErrorMessage] =
    useState('');
  const [formValid, setFormValid] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [passwordValidation, setPasswordValidation] = useState(
    {} as IFieldValidation
  );
  const [passwordConfirmValidation, setPasswordConfirmValidation] = useState(
    {} as IFieldValidation
  );
  const [emailValidation, setEmailValidation] = useState(
    {} as IFieldValidation
  );
  const emailRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const userId = getQuery().get('u') || '';
    if (userId && emailRef.current) {
      setEmail(userId);
      emailRef.current.parentNode
        ?.querySelector(`label[for='${FormIds.email}']`)
        ?.classList.add('active');
    }
  }, [emailRef, local]);

  useEffect(() => {
    const codeParam = getQuery().get('c') || '';
    if (codeParam && codeRef.current) {
      setCode(codeParam);
      codeRef.current.parentNode
        ?.querySelector(`label[for='${FormIds.code}']`)
        ?.classList.add('active');
    }
  }, [codeRef, local]);

  useEffect(() => {
    let allValid = password === passwordConfirm;
    allValid = allValid && code.length > 0;
    allValid = allValid && ValidateEmail(email).class === VALID;
    allValid = allValid && ValidatePassword(password).class === VALID;
    setFormValid(allValid);
  }, [code, email, password, passwordConfirm]);

  const resetPassword = async (event: any) => {
    event.preventDefault();

    try {
      await Auth.forgotPasswordSubmit(email, code, password);
      setResetPasswordErrorMessage('');
      sharedHistory.push(RouteNames.Login);
    } catch (e) {
      setResetPasswordErrorMessage(
        local ? local.t('forgotPasswordCode.resetError') : 'Error'
      );
      console.error(e);
    }
  };

  const validateEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue: string = event.currentTarget.value;
    const validate = ValidateEmail(newValue);
    setEmail(newValue.trim());
    setEmailValidation(validate);
  };

  const validatePasswordConfirm = (newValue: string, pass: string) => {
    const isValid = !newValue || (pass && pass === newValue);
    const validate = {
      class: isValid ? ValidationClasses.VALID : ValidationClasses.INVALID,
      message: isValid
        ? ''
        : (local && local.t('forgotPasswordCode.passwordConfirmError')) ?? '',
    };
    setPasswordConfirm(newValue);
    setPasswordConfirmValidation(validate);
  };

  if (!local) return <></>;

  return (
    <>
      <h4 data-testid='mainTitle'>
        {local && local.t('forgotPasswordCode.title')}
      </h4>
      <div className='row'>
        <div className='col s12'>
          <div className='right'>
            <button
              aria-label={local.t('forgotPasswordCode.cancelButtonText')}
              className='btn btn-tertiary'
              onClick={() => sharedHistory.push(RouteNames.Login)}
              data-testid={FormIds.cancelBtn}
            >
              {local.t('forgotPasswordCode.cancelButtonText')}
            </button>
            <button
              aria-label={local.t('forgotPasswordCode.resetButtonText')}
              className='btn btn-primary'
              onClick={resetPassword}
              disabled={!formValid}
              data-testid={FormIds.resetPasswordBtn}
            >
              {local.t('forgotPasswordCode.resetButtonText')}
            </button>
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col s9'>
          <div className='row'>
            <div className='input-field col s6'>
              <input
                id={FormIds.email}
                ref={emailRef}
                aria-label={local.t('forgotPasswordCode.emailAddressLabel')}
                type='text'
                value={email}
                onChange={validateEmail}
                data-testid={FormIds.email}
                className={emailValidation.class}
              />
              <label htmlFor={FormIds.email}>
                {local.t('forgotPasswordCode.emailAddressLabel')}
              </label>
              <span
                className='helper-text'
                data-error={emailValidation.message}
              >
                {emailValidation.message}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col s9'>
          <div className='row'>
            <div className='input-field col s6'>
              <input
                id={FormIds.code}
                ref={codeRef}
                aria-label={local.t('forgotPasswordCode.codeLabel')}
                type='text'
                value={code}
                data-testid={FormIds.code}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setCode(e.target?.value.trim() || '');
                }}
              />
              <label htmlFor={FormIds.code}>
                {local.t('forgotPasswordCode.codeLabel')}
              </label>
              <span className='helper-text'></span>
            </div>
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col s9'>
          <div className='row'>
            <div className='input-field col s6'>
              <PasswordInput
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const newPass = e.target?.value.trim() || '';
                  setPassword(newPass);
                  setPasswordValidation(ValidatePassword(newPass));
                  validatePasswordConfirm(passwordConfirm, newPass);
                }}
                id={FormIds.password}
                ariaLabel={FormIds.password}
                testId={FormIds.password}
                inputClassName={passwordValidation.class}
                helperText={passwordValidation.message}
                value={password}
                labelText={local.t('forgotPasswordCode.passwordLabel')}
              />
            </div>
          </div>
        </div>
        <div className='row'>
          <div className='col s9'>
            <div className='row'>
              <div className='input-field col s6'>
                <PasswordInput
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    validatePasswordConfirm(e.target?.value.trim() || '', password);
                  }}
                  id={FormIds.passwordConfirm}
                  ariaLabel={FormIds.passwordConfirm}
                  testId={FormIds.passwordConfirm}
                  inputClassName={'' + passwordConfirmValidation.class}
                  helperText={passwordConfirmValidation.message}
                  value={passwordConfirm}
                  labelText={local.t('forgotPasswordCode.passwordConfirmLabel')}
                />
                {resetPasswordErrorMessage.length > 0 ? (
                  <div className='reset-error' data-testid='resetError'>
                    {resetPasswordErrorMessage}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default withNavBar(ForgotPasswordCode);
